<template>
    <div
        :class="['flex justify-center flex-col', hasAdBlocker ? 'w-[80%]' : '']"
        @click.capture="trackJobSeekerStatus"
    >
        <slot name="xandr-ad-button"></slot>
        <div
            :id="xandrWrapper"
            :class="[
                'relative',
                isFullScreenAd ? '' : 'min-w-[320px] md:min-w-full',
                isSponsoredHeader ? 'hidden' : '',
            ]"
        >
            <div v-if="canShowPremiumAds && hasAdBlocker && !isPremiumUser">
                <div
                    class="cursor-pointer"
                    @click.stop="handleAdClick"
                >
                    <img
                        :src="getPremiumImgPath()"
                        class="w-full"
                        @load="loadAndResizeHostImage"
                    />
                </div>
            </div>
            <div
                v-else
                :id="xandrSlot"
                class="inline"
                @click.stop="handleAdClick"
            ></div>
        </div>
        <slot
            name="xandr-ad-footer"
            :has-ad="xandrData[xandrSlot]?.hasAd"
        >
            <div
                v-if="canShowReportModal"
                class="flex flex-row"
            >
                <div class="flex self-start border bg-white shadow">
                    <Btn
                        v-if="!isSponsoredHeader"
                        class="!text-xs"
                        color="slate-50"
                        size="xxs"
                        left-icon="info"
                        :label="t('dsa-info-text')"
                        @click="openDsaInfo"
                    >
                    </Btn>
                </div>
                <div class="flex ml-auto border bg-white shadow border-b border-l border-r rounded-b">
                    <Btn
                        v-if="!isReported"
                        color="slate-50"
                        size="xxs"
                        left-icon="flag"
                        @click="emitReportAd"
                    >
                    </Btn>
                    <span
                        :class="[
                            {
                                hidden: isReported,
                            },
                            'text-slate-300 inline-block',
                        ]"
                        >|</span
                    >
                    <Btn
                        color="slate-50"
                        left-icon="close"
                        size="xxs"
                        @click="adPremiumClick"
                    >
                    </Btn>
                </div>
            </div>
        </slot>
    </div>
</template>

<script>
import XandrMixin from '@components/Xandr/XandrMixin';
import { renderAd } from '@/modules/xandr';
import { trackEvent } from '@/modules/tracking';
import { useTranslation } from 'i18next-vue';

export default {
    name: 'Xandr',

    extends: XandrMixin,

    setup() {
        const { t } = useTranslation('dsa');
        return { t };
    },

    mounted() {
        // create self-hosted premium ads
        if (this.showPremiumAds) {
            this.createFakeXandrData(this.xandrSlot);
            // track impression of self-hosted ads
            this.$nextTick(() => {
                trackEvent('xandr_ads', 'xandr_ad_block_impression', '', {
                    ad_type: this.xandrSlot,
                });
            });
        }

        if (this.adOnDemand) {
            const previousElement = this.$el?.previousElementSibling
                ? this.$el.previousElementSibling
                : this.$el.parentElement?.previousElementSibling;
            if (previousElement) {
                // we will request an ad when part of the previous element will be visible.
                this.requestXandrAd(previousElement);
            }
        }
        // due to the dashboard ads are not part of the ad-on-demand logic, we need to request them manually
        if (this.isDashboardAd) {
            this.requestXandrDashboardAd();
        }
    },

    updated() {
        // temporary fix to remove the extra height we get when xandr inserts new ads
        if (document.getElementById(this.xandrSlot)?.hasAttribute('style')) {
            document.getElementById(this.xandrSlot).removeAttribute('style');
            const slotSpacerId = `${this.xandrSlot}_slotSpacer`;
            const slotSpacerElement = document.getElementById(slotSpacerId);
            if (slotSpacerElement) {
                slotSpacerElement.style.display = 'none'; // hide the element
            }
        }
    },

    methods: {
        requestXandrAd(element) {
            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.intersectionRatio > 0 && !this.isVisible) {
                        renderAd(this.xandrSlot, this.xandrSizes);
                        this.isVisible = true;
                        observer.unobserve(entry.target);
                    }
                });
            }, this.options);

            observer.observe(element);
        },
        requestXandrDashboardAd() {
            renderAd(this.xandrSlot, this.xandrSizes);
        },
    },
};
</script>

<style lang="scss" scoped>
/**
 we need to overwrite the base image-rendering css for firefox/safari
 */
:deep(img) {
    image-rendering: auto !important;
}
</style>
